import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { FileText, FolderKanban, DollarSign, Share2, LogOut, Layers, ExternalLink, Heart, LayoutDashboard, Users, Flag, Settings, BadgeCheck } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCrushShyT } from '../../hooks/useCrushShyT';

const AdminLayout = () => {
  const { isAuthenticated, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useCrushShyT();

  useEffect(() => {
    if (!loading && !isAuthenticated && !location.pathname.includes('/admin/login')) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, loading, navigate, location]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const menuItems = [
    { icon: FileText, label: 'Content', path: '/admin/content' },
    { icon: FolderKanban, label: 'Projects', path: '/admin/projects' },
    { icon: Layers, label: 'Services', path: '/admin/services' },
    { icon: DollarSign, label: 'Pricing', path: '/admin/pricing' },
    { icon: Share2, label: 'Socials', path: '/admin/socials' }
  ];

  const crushShyItems = [
    { icon: LayoutDashboard, label: t('navOverview'), path: '/admin/crushshy/dashboard' },
    { icon: Users, label: t('navUsers'), path: '/admin/crushshy/users' },
    { icon: Flag, label: t('navReports'), path: '/admin/crushshy/reports' },
    { icon: BadgeCheck, label: 'Vérifications', path: '/admin/crushshy/verifications' },
    { icon: Settings, label: t('navSettings'), path: '/admin/crushshy/settings' },
  ];

  const isCrushShyRoute = location.pathname.startsWith('/admin/crushshy');

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-gradient-to-b from-[#1a1a1b] to-[#0f0f10] border-r border-cyan-500/10 flex flex-col">
        <div className="p-6 border-b border-cyan-500/10">
          <Link
            to="/"
            title="Ouvrir le site public"
            className="group block rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500/40"
          >
            <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent group-hover:from-cyan-300 group-hover:to-blue-400 transition-all">
              Loranrix
            </h1>
            <p className="text-sm text-gray-400 mt-1 flex items-center gap-1.5 group-hover:text-cyan-400/90 transition-colors">
              <ExternalLink className="w-3.5 h-3.5 opacity-70" aria-hidden />
              Voir le site
            </p>
          </Link>
          <p className="text-xs text-gray-500 mt-2">Admin Panel</p>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link key={item.path} to={item.path}>
                <motion.div
                  whileHover={{ x: 4 }}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/30'
                      : 'text-gray-400 hover:bg-cyan-500/5 hover:text-cyan-300'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </motion.div>
              </Link>
            );
          })}

          <div className="pt-4 mt-2 border-t border-pink-500/15">
            <div className="flex items-center gap-2 px-4 py-2 mb-1">
              <Heart className="w-4 h-4 text-pink-400" fill="currentColor" />
              <span className="text-xs font-bold uppercase tracking-wide text-pink-400/90">CrushShy</span>
            </div>
            {crushShyItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path}>
                  <motion.div
                    whileHover={{ x: 4 }}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      isActive
                        ? 'bg-pink-500/15 text-pink-300 border border-pink-500/35'
                        : isCrushShyRoute
                          ? 'text-pink-200/70 hover:bg-pink-500/8 hover:text-pink-200'
                          : 'text-gray-400 hover:bg-pink-500/5 hover:text-pink-300'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </motion.div>
                </Link>
              );
            })}
          </div>
        </nav>

        <div className="p-4 border-t border-cyan-500/10">
          <motion.button
            onClick={handleLogout}
            whileHover={{ x: 4 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-500/10 w-full transition-all"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Logout</span>
          </motion.button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
